import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  CheckCircle,
  CircleAlert,
  Download,
  ExternalLink,
  Eye,
  EyeOff,
  Globe2,
  KeyRound,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

const RM_LOGIN_URL = 'https://rm.academiacortex.com.ar';
const APP_STORE_URL = 'https://apps.apple.com/app/id6778114798';

export function RmPasswordSetup({
  email,
  configured = false,
  onSubmit,
  onConfigured,
}) {
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [configuredLocally, setConfiguredLocally] = useState(false);
  const isConfigured = configured || configuredLocally;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (password.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (password !== passwordConfirm) {
      toast.error('Las contraseñas no coinciden.');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await onSubmit(password);
      setPassword('');
      setPasswordConfirm('');
      setConfiguredLocally(true);
      onConfigured?.();
      toast.success(
        result?.already_configured
          ? 'Tu acceso ya estaba creado.'
          : 'Tu contraseña fue creada correctamente.'
      );
    } catch (error) {
      toast.error(
        error?.response?.data?.detail
        || error?.message
        || 'No pudimos crear tu contraseña. Probá de nuevo.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isConfigured) {
    return (
      <Card className="border-green-200 bg-green-50/70 dark:border-green-900 dark:bg-green-950/20">
        <CardHeader>
          <div className="flex items-start gap-3">
            <CheckCircle className="mt-0.5 h-6 w-6 shrink-0 text-green-600" />
            <div className="space-y-1.5">
              <CardTitle>Tu acceso a la app está listo</CardTitle>
              <CardDescription>
                Ingresá a Reprogramación Mental con <strong>{email}</strong> y la contraseña que elegiste.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-md border border-green-200 bg-background/70 p-4 dark:border-green-900">
            <p className="text-sm text-muted-foreground">Ingresá desde la web:</p>
            <a
              href={RM_LOGIN_URL}
              target="_blank"
              rel="noreferrer"
              className="mt-1 inline-flex items-center gap-2 break-all font-semibold text-primary underline-offset-4 hover:underline"
            >
              rm.academiacortex.com.ar
              <ExternalLink className="h-4 w-4 shrink-0" />
            </a>
            <p className="mt-3 text-sm text-muted-foreground">
              Si tenés iPhone, también podés descargar la app desde App Store.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button asChild className="w-full sm:w-auto">
              <a href={RM_LOGIN_URL} target="_blank" rel="noreferrer">
                <Globe2 className="h-4 w-4" />
                Ingresar desde la web
              </a>
            </Button>
            <Button asChild variant="outline" className="w-full sm:w-auto">
              <a href={APP_STORE_URL} target="_blank" rel="noreferrer">
                <Download className="h-4 w-4" />
                Descargar para iPhone
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="rm-password-attention border-rose-400 bg-rose-50/60 dark:border-rose-600 dark:bg-rose-950/15">
      <CardHeader>
        <div className="flex items-start gap-3">
          <CircleAlert className="mt-0.5 h-6 w-6 shrink-0 text-rose-600 dark:text-rose-400" />
          <div className="space-y-2">
            <span className="inline-flex items-center rounded border border-rose-300 bg-rose-100 px-2 py-1 text-xs font-bold uppercase text-rose-800 dark:border-rose-800 dark:bg-rose-950/60 dark:text-rose-200">
              Paso primordial
            </span>
            <CardTitle>Creá tu contraseña para acceder a la app</CardTitle>
            <CardDescription>
              <strong className="text-foreground">Es primordial completar este paso ahora.</strong>{' '}
              Tu usuario será <strong>{email}</strong>. Elegí una contraseña para ingresar a Reprogramación Mental.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="rm-password">Contraseña</Label>
              <div className="relative">
                <Input
                  id="rm-password"
                  name="rm-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="new-password"
                  minLength={8}
                  className="pr-10"
                  disabled={isSubmitting}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((visible) => !visible)}
                  className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">Mínimo 8 caracteres.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="rm-password-confirm">Repetir contraseña</Label>
              <Input
                id="rm-password-confirm"
                name="rm-password-confirm"
                type={showPassword ? 'text' : 'password'}
                value={passwordConfirm}
                onChange={(event) => setPasswordConfirm(event.target.value)}
                autoComplete="new-password"
                minLength={8}
                disabled={isSubmitting}
                required
              />
            </div>
          </div>
          <Button type="submit" disabled={isSubmitting || !email} className="w-full sm:w-auto">
            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
            Crear contraseña
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

export default RmPasswordSetup;
